import { type FC, useState } from "react";

import { DateTimeFormatter } from "@styled/date_time_formatter";
import {
  IntakeOutlineEntry,
  IntakeOutlineFlag,
  IntakeOutlinePart,
  IntakeOutlineParts,
  IntakeTranscriptLine,
} from "@styled/intake_outline";
import { Segmented } from "@styled/segmented";

import { IntakePane } from "../intake_pane";
import { type Interview } from "./types";

// Never "Assistant": that names the app's own assistants.
const SPEAKERS: Record<string, string> = {
  assistant: "AI interviewer",
  claimant: "Claimant",
};

// The intake outline: the claimant's answers by part, or the full transcript.
// Always inside a section that is already a card.
export const IntakeOutlineView: FC<{ interview: Interview }> = ({
  interview,
}) => {
  const [view, setView] = useState<"answers" | "transcript">("answers");

  return (
    <IntakePane
      flat
      title="AI intake outline"
      subtitle="Review the entire outline before the exam."
      controls={
        <Segmented
          items={[
            { value: "answers", label: "Answers" },
            { value: "transcript", label: "Transcript" },
          ]}
          selectedValue={view}
          onChange={(value) => setView(value as "answers" | "transcript")}
        />
      }
    >
      {view === "answers" ? (
        <IntakeAnswers interview={interview} />
      ) : (
        <IntakeTranscript interview={interview} />
      )}
    </IntakePane>
  );
};

const IntakeAnswers: FC<{ interview: Interview }> = ({ interview }) => (
  <IntakeOutlineParts>
    {interview.outline.map((part) => (
      <IntakeOutlinePart
        key={part.number}
        label={`Part ${part.number} · ${part.name}`}
      >
        {part.entries.map((entry) => (
          <IntakeOutlineEntry
            key={entry.questionId}
            question={entry.question}
            answer={entry.answer?.value.join(", ")}
            words={entry.answer?.claimantWords}
            flagged={entry.flags.length > 0}
          >
            {entry.flags.map((flag) => (
              <IntakeOutlineFlag key={flag.id}>{flag.note}</IntakeOutlineFlag>
            ))}
          </IntakeOutlineEntry>
        ))}
      </IntakeOutlinePart>
    ))}
  </IntakeOutlineParts>
);

const IntakeTranscript: FC<{ interview: Interview }> = ({ interview }) => (
  <>
    {interview.transcriptLines.map((line) => (
      <IntakeTranscriptLine
        key={line.id}
        speaker={SPEAKERS[line.speaker] ?? line.speaker}
        at={<DateTimeFormatter iso={line.createdAt} format="time" />}
      >
        {line.text}
      </IntakeTranscriptLine>
    ))}
  </>
);
